import {
  BibleSiteGettable,
  BibleSitePrecacher,
  BibleSiteStorable
} from "./typedefs/BibleSiteStorage";
import {
  OfflineState,
  Site,
  Bible,
  Book,
  Chapter
} from "./typedefs/BibleOffline";
import {
  Status,
  LATER,
  NOT_FOUND,
  success,
  CACHED,
  LOCAL,
  QUEUED,
  FAILURE,
  NEVER
} from "./typedefs/RequestStatus";
import { Queueable, Job, JobX } from "./typedefs/Queuable";
import { TaskQueue } from "./queue";
import { isBibleWithText, isBibleWithMetadata } from "./typedefs/Bible";
import { Passthrough } from "./passthrough";

/* LATER: this first caching gettable design could be factored to better handle cases where

  1. We don't have a local store (e.g. a non-PWA web build).
  2. We have a local store, but it can't cache new Bibles (e.g. a Cordova build).

  In both cases we could probably get buy just hiding the download buttons, but IWBNI we
  prevented calls to the caching layer from excepting.

  The current version does this by exposing .canCache() which is not in any interface (yet)
  and introducing a a Passthrough store that never has the text locally but always
  successfully passes the request down to then API.
*/

interface DownloadTracker {
  bible: Bible;
  remainingChapters: number;
}

export class Downloader
  implements BibleSiteGettable, BibleSitePrecacher, Queueable {
  private cache: BibleSiteStorable;
  private queue: TaskQueue;
  private biblesDownloading: { [bibleId: string]: DownloadTracker } = {};
  private sitesSeen: { [domain: string]: Site } = {}; // to save having to iterate sites when a bible has been downloaded

  constructor(
    cache: BibleSiteStorable | undefined,
    private sources: BibleSiteGettable[]
  ) {
    this.cache = cache || Passthrough;

    const queue = (this.queue = new TaskQueue(this.jobToExecutable));
    queue.setOnSuccess(this.onJobSuccess.bind(this));
  }

  // true if this platform and build can actually download Bibles
  canCache(): boolean {
    return this.cache !== Passthrough;
  }

  //===== FETCH HELPERS: get data from (possibly) remote sources bypassing the cache
  async getRemoteSite(fullSiteDomain: string) {
    let site: Site | undefined = undefined,
      status: Status = NOT_FOUND;
    for (let source of this.sources) {
      ({ site, status } = await source.getSite(fullSiteDomain));
      if (success(status)) break;
    }
    if (site) this.sitesSeen[site.domain] = site;
    return { site, status };
  }

  async getRemoteBible(bibleId: string) {
    let bible: Bible | undefined = undefined,
      status: Status = NOT_FOUND;
    for (let source of this.sources) {
      ({ bible, status } = await source.getBible(bibleId));
      if (success(status)) break;
    }
    return { bible, status };
  }

  async getRemoteBooks(
    bibleId: string
  ): Promise<{ books: Book[] | undefined; status: Status }> {
    let books: Book[] | undefined = undefined,
      status: Status = NOT_FOUND;
    for (let source of this.sources) {
      ({ books, status } = await source.getBooks(bibleId));
      if (success(status)) break;
    }
    return { books, status };
  }

  async getRemoteChapter(
    bibleId: string,
    chapterId: string,
    parallelBibleIds: string[] = []
  ): Promise<{ chapter: Chapter | undefined; status: Status }> {
    let chapter: Chapter | undefined = undefined,
      status: Status = NOT_FOUND;
    for (let source of this.sources) {
      ({ chapter, status } = await source.getChapter(
        bibleId,
        chapterId,
        parallelBibleIds
      ));
      if (success(status)) break;
    }
    return { chapter, status };
  }

  //===== SITE SOURCE METHODS =====

  // Get Bible site metadata, including Bibles (shallow, without books and chapters)Site
  async getSite(
    fullSiteDomain: string
  ): Promise<{ site: Site | undefined; status: Status }> {
    let { site, status } = await this.cache.getSite(fullSiteDomain);
    if (!success(status))
      ({ site, status } = await this.getRemoteSite(fullSiteDomain));
    if (site) this.sitesSeen[site.domain] = site;
    return { site, status };
  }

  // Get Bible info and related audio bibles
  async getBible(
    bibleId: string
  ): Promise<{ bible: Bible | undefined; status: Status }> {
    let { bible, status } = await this.cache.getBible(bibleId);
    if (!success(status))
      ({ bible, status } = await this.getRemoteBible(bibleId));
    return { bible, status };
  }

  // Get details book and chapter info about the Bible (but no text in the chapters)
  async getBooks(
    bibleId: string
  ): Promise<{ books: Book[] | undefined; status: Status }> {
    let { books, status } = await this.cache.getBooks(bibleId);
    if (!success(status))
      ({ books, status } = await this.getRemoteBooks(bibleId));
    return { books, status };
  }

  // Get the text of a chapter in the JSON format parsed into a JavaScript object
  async getChapter(
    bibleId: string,
    chapterId: string,
    parallelBibleIds: string[] = []
  ): Promise<{ chapter: Chapter | undefined; status: Status }> {
    let { chapter, status } = await this.cache.getChapter(
      bibleId,
      chapterId,
      parallelBibleIds
    );
    for (let source of this.sources) {
      if (success(status)) break;
      ({ chapter, status } = await source.getChapter(
        bibleId,
        chapterId,
        parallelBibleIds
      ));
    }
    return { chapter, status };
  }

  //===== SITE PRECACHER METHODS =====

  // fetch metadata for all bibles and queue downloads for all chapter text
  async deepCacheSite(
    fullSiteDomain: string
  ): Promise<{ site: Site | undefined; status: Status }> {
    let { site, status } = await this.cacheSite(fullSiteDomain);
    console.warn("Not tested yet.");
    if (site)
      for (let bible of site.bibles || [])
        this.enqueue(["deepCacheBible", bible.id]);
    return { site, status };
  }

  // only handles shallow caching, the fetch of site metadata that we always do
  async cacheSite(
    fullSiteDomain: string
  ): Promise<{ site: Site | undefined; status: Status }> {
    // check for a site locally
    let { site, status } = await this.cache.getSite(fullSiteDomain);

    // ...and mark it LOCAL
    if (success(status)) {
      status = LOCAL;
    }
    // ... or try get it remotely
    else {
      ({ site, status } = await this.getRemoteSite(fullSiteDomain));
      if (success(status) && site && this.canCache()) {
        site.offlineState = OfflineState.local;
        site.deepOfflineState = OfflineState.remote;
        status = (await this.cache.setSite(site)) ? CACHED : FAILURE;
      }
    }
    // note the site if we got one
    if (site) this.sitesSeen[site.domain] = site;

    return { site, status };
  }

  isTextDownloaded(item: Bible): boolean {
    return !!(
      isBibleWithText(item) &&
      item.deepOfflineState === OfflineState.local &&
      item.offlineState === OfflineState.local
    );
  }

  async deepCacheBible(
    bibleId: string
  ): Promise<{ bible: Bible | undefined; status: Status }> {
    if (!this.canCache()) {
      return { bible: undefined, status: NEVER };
    }
    // take care of caching the bible metadata
    let { bible, status } = await this.cacheBible(bibleId);
    if (!bible) {
      console.debug("unexpected state: cacheBible returned a blank bible");
      return { bible, status };
    }

    try {
      if (
        !(
          bible.offlineState === OfflineState.local &&
          isBibleWithMetadata(bible)
        )
      ) {
        console.debug("bible still downloading metadata");
        return { bible, status: LATER };
      }

      // queue the chapter downloads
      let remaining = 0;
      for (let book of bible.books || []) // loop through books
        if (book.deepOfflineState !== OfflineState.local)
          for (let chapter of book.chapters || []) //  ... and chapters in each book
            if (chapter.deepOfflineState !== OfflineState.local) {
              remaining++;
              this.enqueue(["cacheChapter", bibleId, chapter.id]); // enqueue the download
            }

      if (remaining > 0) {
        // set up download progress tracking
        const tracker: DownloadTracker = {
          bible: bible,
          remainingChapters: remaining
        };
        this.biblesDownloading[bible.id] = tracker;
        // mark bible as being downloaded
        bible.deepOfflineState = OfflineState.downloading;
        await this.cache.setBible(bible);
        status = QUEUED;
      } else {
        // user requested Bible download, but we actually have everything
        console.debug("bible appears to already be local");
        status = LOCAL;
        await this.onBibleDownload(bibleId); // fire any listeners in case UI misled user to re-request download
      }

      return { bible, status };
    } catch (e) {
      console.error("error queuing chapter downloads");
      return { bible: undefined, status: FAILURE };
    }
  }

  async onChapterDownload(bibleId: string, chapterId: string): Promise<void> {
    const tracker = this.biblesDownloading[bibleId];
    if (tracker && tracker.bible) {
      tracker.remainingChapters--;
      try {
        if (tracker.bible.books) {
          const bookId = chapterId.split(".")[0]; // should this use a parser function so it's not coupled to chapter representation?
          const book = tracker.bible.books.find(b => b.id === bookId);
          if (book && book.chapters) {
            const chapter = book.chapters.find(c => c.id === chapterId);
            if (chapter)
              chapter.offlineState = chapter.deepOfflineState =
                OfflineState.local;
          }
        }
      } finally {
        if (tracker.remainingChapters <= 0) {
          await this.cache.setBible(tracker.bible);
          await this.onBibleDownload(tracker.bible.id);
        }
      }
      // LATER: here's where a call to progress percent tracking would go
    }
  }

  async onBibleDownload(bibleId: string): Promise<void> {
    console.debug("Bible finished downloading", bibleId);
    for (let sitedomain in this.sitesSeen) {
      const site = this.sitesSeen[sitedomain];
      for (let bibleMeta of site.bibles || []) {
        if (bibleMeta.id === bibleId) {
          bibleMeta.offlineState = bibleMeta.deepOfflineState =
            OfflineState.local;
          await this.onSiteUpdate(site, bibleMeta);
        }
      }
    }
  }

  private _siteUpdatedCallback?: (site: Site) => void;

  // IWBNI we could use a setter here, but CRA's Babel setup transpiles them wrong.
  setSiteUpdatedCallback(cb: (site: Site) => void) {
    this._siteUpdatedCallback = cb;
  }

  async onSiteUpdate(site: Site, siteBibleMetadata?: Bible) {
    console.debug("Site notified of download finished", site);
    await this.cache.setSite(site);
    if (this._siteUpdatedCallback) this._siteUpdatedCallback(site);
  }

  async cacheBible(
    bibleId: string
  ): Promise<{ bible: Bible | undefined; status: Status }> {
    let { bible, status } = await this.cache.getBible(bibleId);
    if (success(status)) return { bible, status: LOCAL };

    ({ bible, status } = await this.getBible(bibleId));
    if (success(status) && bible) {
      bible.offlineState = OfflineState.downloading; // LATER mark .downloading until books and chapters fetched.
      bible.deepOfflineState = OfflineState.remote;
      status = (await this.cache.setBible(bible)) ? CACHED : FAILURE;
      // this can't be queued because we'll often do this within a deepCacheBible,
      // and the chapter download jobs won't queue if this hasn't happened since
      // the chapters metadata present yet
      // this.enqueue(["cacheBooks", bibleId]);
      let books: Book[] | undefined;
      ({ books, status } = await this.cacheBooks(bibleId));
      if (!books)
        console.error("cached bible, but failed to cache books", bible);
      else {
        bible.offlineState = OfflineState.local;
        bible.books = books;
      }
    }

    return { bible, status };
  }

  async booksAndChaptersLookValid(
    bibleId: string,
    books: Book[] | undefined
  ): Promise<boolean> {
    if (!books) return false;
    if (books.length < 1) return false;
    return true;
  }

  async cacheBooks(
    bibleId: string
  ): Promise<{ books: Book[] | undefined; status: Status }> {
    // return the local copy if we have it
    let { books, status } = await this.cache.getBooks(bibleId);
    if (await this.booksAndChaptersLookValid(bibleId, books))
      return { books, status: LOCAL };

    // grab the books and chapters metadata
    ({ books, status } = await this.getRemoteBooks(bibleId));
    if (books && books.length > 0 && success(status)) {
      books = books || [];
      for (let book of books) {
        book.offlineState = OfflineState.local;
        book.deepOfflineState = OfflineState.remote;
        for (let chapter of book.chapters || []) {
          chapter.offlineState = OfflineState.remote;
        }
      }

      // setBooks implicitly marks the bible .local, does that concern belong here?
      // or maybe in a base class
      status = (await this.cache.setBooks(bibleId, books)) ? CACHED : FAILURE;
    }

    return { books, status };
  }

  async cacheChapter(
    bibleId: string,
    chapterId: string,
    parallelBibleIds: string[] = []
  ): Promise<{ chapter: Chapter | undefined; status: Status }> {
    // BUG: passing through the parallelBibleIds fixes parallels not
    // showing up online https://app.clickup.com/t/1r4jbt
    // but the cache system will need to strip out parallels
    // before storing (or come up with a strategy to store them)
    // so this HACK forces NO parallels until we figure that out
    parallelBibleIds = [];

    let { chapter, status } = await this.cache.getChapter(
      bibleId,
      chapterId,
      parallelBibleIds
    );
    if (chapter && success(status)) return { chapter, status: LOCAL };

    ({ chapter, status } = await this.getRemoteChapter(
      bibleId,
      chapterId,
      parallelBibleIds
    ));
    if (success(status) && chapter) {
      // console.log("CHAPTER=", chapter);
      // LATER strip or store parallels here
      // chapter.parallels = []
      chapter.offlineState = chapter.deepOfflineState = OfflineState.local;
      if (await this.cache.setChapter(chapter)) status = CACHED;
    }
    return { chapter, status };
  }

  //===== QUEUE MANAGEMENT

  // functions delegated to the TaskQueue
  enqueue = (job: string[]) => this.queue.enqueue(job); //(job)queue.enqueue.bind(queue);
  step = () => this.queue.step(); //Promise<number>;
  start = () => this.queue.start();
  pause = () => this.queue.pause();
  pending = () => this.queue.pending();
  peek = () => this.queue.peek();

  onJobSuccess(status: Status, job: Job): void {
    // console.debug(status, job)
    if ((job[0] = "cacheChapter")) {
      this.onChapterDownload(job[1], job[2]);
    }
  }

  private methodMap: {
    [methodName: string]: (...args: string[]) => Promise<Status>;
  } = {
    cacheBible: async (bibleId: string) => {
      const { status } = await this.cacheBible(bibleId);
      return status;
    },
    deepCacheBible: async (bibleId: string) => {
      let { status } = await this.cacheBible(bibleId);
      if (success(status)) ({ status } = await this.deepCacheBible(bibleId));
      return status;
    },
    cacheBooks: async (bibleId: string) => {
      const { status } = await this.cacheBooks(bibleId);
      return status;
    },
    cacheChapter: async (bibleId: string, chapterId: string) => {
      const { status } = await this.cacheChapter(bibleId, chapterId);
      return status;
    }
  };

  private jobToExecutable = (job: Job): JobX => {
    const jobName = job[0];
    const jobArgs = job.slice(1);
    const jobMethod = this.methodMap[jobName]; // do we need the bind here of do the closures above take care of that?

    return async function(): Promise<Status> {
      try {
        if (!jobMethod) return FAILURE;
        const status = await jobMethod(...jobArgs);
        return status;
      } catch (e) {
        console.error("error running job", e);
        throw e;
      }
    };
  };
}
